'use client'

import { useCallback } from 'react'
import { useParams, useRouter, useSearchParams } from 'next/navigation'

import { sanitizeHotelCode } from 'utils/sanitizeSearchParams'

export type AppNavigateOptions = {
  replace?: boolean
}

export type UseAppNavigateResult = {
  navigate: (url: string, options?: AppNavigateOptions) => void
}

// Root layout (app/(pages)/[locale]/layout.tsx) decides whether to inject the
// Tripla chatbot script server-side, based on locale + hotelCode read from the
// request URL. That script only toggles on a full page load, so any navigation
// that changes locale or hotelCode must force a hard browser navigation instead
// of a soft client-side transition.
const useAppNavigate = (): UseAppNavigateResult => {
  const router = useRouter()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}
  const searchParams = useSearchParams()

  const navigate = useCallback(
    (url: string, options?: AppNavigateOptions): void => {
      const targetUrl = new URL(url, window.location.origin)
      const targetLocale = targetUrl.pathname.split('/')[1]

      const currentHotelCode = sanitizeHotelCode(searchParams?.get('hotelCode'))
      const targetHotelCode = sanitizeHotelCode(
        targetUrl.searchParams.get('hotelCode'),
      )

      const isLocaleChanged = targetLocale !== locale
      const isHotelCodeChanged =
        (currentHotelCode ?? '') !== (targetHotelCode ?? '')

      if (isLocaleChanged || isHotelCodeChanged) {
        if (options?.replace) {
          window.location.replace(url)
        } else {
          window.location.assign(url)
        }
        return
      }

      if (options?.replace) {
        router.replace(url)
      } else {
        router.push(url)
      }
    },
    [locale, router, searchParams],
  )

  return { navigate }
}

export default useAppNavigate
